import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * "End call" — hang up an outbound conversation that is running (owner request 2026-10-28, WAPP-ENDCALL).
 *
 * Pausing or ending a campaign never hangs up a call in progress; this does, one conversation at a time. Offered on the
 * agent's Live tab and on Live activity, on a row whose `outboundOrigin` is set (campaign, on-demand or a trunk test
 * call — exactly what `POST /v1/outbound-conversations/{conversation_id}/end` accepts), to a person who may dial
 * (`CONSOLE_ACT["conversation.dial"]`, the dial routes' own role list). Never on an inbound conversation.
 *
 * A confirm dialog first ("End this call?"). Then `endOutboundConversation`: while it is pending both buttons are
 * disabled; on success the row is hidden at once (`onEnded`) and the live reads are asked again, so it leaves for good on
 * the next refresh. A 404 means the call has already ended — said in words, and the row hidden just the same. Any other
 * refusal (the runtime not answering, `503 agent_runtime_unavailable`, is retryable) is said in the API's own words, from
 * the errors catalog, and the dialog stays open so the person can press again.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button, Dialog, Icon } from "@wetalk/ui";
import type { OutboundOrigin } from "@wetalk/domain";
import { useState, type ReactNode } from "react";

import { apiErrorText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import { QUERY_KEY } from "../../../api/query.js";
import { GLYPH } from "../../../shell/glyph.js";

export interface EndOutboundConversationProps {
  readonly conversationId: string;
  readonly outboundOrigin: OutboundOrigin;
  /** How the row names the far end ("+30 694 …", "Caller hidden"), for the button's accessible name. */
  readonly partyText: string;
  /** The conversation has ended (or had already): the row hides it now, ahead of the next live read. */
  readonly onEnded: (conversationId: string) => void;
}

/** Whether a refusal says the conversation is no longer there to end: it has already ended. */
export function isAlreadyEnded(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** The sentence a failed end shows. */
export function endConversationRefusalText(error: unknown): string {
  if (isAlreadyEnded(error)) return portalText("operations", "end_call.already_ended");
  return apiErrorText(error, portalText("operations", "end_call.failed"));
}

export function EndOutboundConversation({ conversationId, outboundOrigin, partyText, onEnded }: EndOutboundConversationProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY.liveSnapshot });
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY.liveActivity });
  };

  const end = useMutation({
    mutationFn: () => gateway.endOutboundConversation(conversationId),
    onSuccess: () => {
      setOpen(false);
      onEnded(conversationId);
      refresh();
    },
    onError: (error) => {
      // Nothing left to end: the row goes as it would have on the next read. The words stay until the dialog closes.
      if (isAlreadyEnded(error)) refresh();
    },
  });

  const close = (): void => {
    setOpen(false);
    if (isAlreadyEnded(end.error)) onEnded(conversationId);
  };

  return (
    <>
      <Button
        variant="secondary"
        aria-label={portalText("operations", "end_call.action_label", { party: partyText })}
        disabled={end.isPending}
        onClick={() => {
          end.reset();
          setOpen(true);
        }}
      >
        <Icon glyph={GLYPH.phoneDisconnect} size="md" />
        <span>{portalText("operations", "end_call.action")}</span>
      </Button>
      {open ? (
        <Dialog
          open
          title={portalText("operations", "end_call.title")}
          dismissible={!end.isPending}
          onClose={close}
          actions={
            <>
              <Button variant="secondary" disabled={end.isPending} onClick={close}>
                {portalText("operations", "end_call.keep")}
              </Button>
              {isAlreadyEnded(end.error) ? null : (
                <Button
                  variant="primary"
                  disabled={end.isPending}
                  onClick={() => {
                    end.mutate();
                  }}
                >
                  {end.isPending ? portalText("operations", "end_call.working") : portalText("operations", "end_call.confirm")}
                </Button>
              )}
            </>
          }
        >
          <p className="c-note">
            {outboundOrigin === "campaign"
              ? portalText("operations", "end_call.body_campaign")
              : portalText("operations", "end_call.body")}
          </p>
          {end.isError ? (
            <p className={isAlreadyEnded(end.error) ? "c-note" : "c-note c-note-critical"} role="alert">
              {endConversationRefusalText(end.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </>
  );
}
